import { RoutesPaths } from "../../../../constants/routes.ts"
import { type CompanyDefaultsBlockKey } from "../../types/company-defaults-access.types.ts"
import { type TemplateCountKey } from "../settings-templates/use-template-counts.ts"

export type CompanyDefaultsSubtab = "configuration" | "templates"

export type ConfigurationTab = "ids" | "projects" | "subcontractors" | "sms"

interface ConfigurationPlacement {
  subtab: "configuration"
  // Company HQ (and a single-operation company) edits the company's own
  // defaults; a branch workspace edits that branch's effective configuration.
  workspace: "company" | "branch"
  // The inner Configurations tab; absent for the card above the tabs.
  tab?: ConfigurationTab
}

// Status automation and Required Information render under Settings →
// Automation; their access is still decided per block like every other.
interface AutomationPlacement {
  subtab: "automation"
  section: "status" | "requiredInformation"
}

interface TemplatePlacement {
  subtab: "templates"
  route: string
  card: TemplateCountKey
  needsFeatureFlag?: boolean
}

export type CompanyDefaultsPlacement =
  | ConfigurationPlacement
  | AutomationPlacement
  | TemplatePlacement

// Where every block lives in the UI. A block the server serves that is not
// listed here is never shown; a new block needs its own entry.
export const COMPANY_DEFAULTS_BLOCKS: Record<
  CompanyDefaultsBlockKey,
  CompanyDefaultsPlacement
> = {
  company_wide: { subtab: "configuration", workspace: "company" },
  id_configuration: {
    subtab: "configuration",
    workspace: "company",
    tab: "ids"
  },
  project_list: {
    subtab: "configuration",
    workspace: "company",
    tab: "projects"
  },
  subcontractor_alerts: {
    subtab: "configuration",
    workspace: "company",
    tab: "subcontractors"
  },
  sms_retention: { subtab: "configuration", workspace: "company", tab: "sms" },
  sms_send_window: {
    subtab: "configuration",
    workspace: "company",
    tab: "sms"
  },
  status_automation: { subtab: "automation", section: "status" },
  branch_configuration: { subtab: "configuration", workspace: "branch" },
  required_information: {
    subtab: "automation",
    section: "requiredInformation"
  },
  project_templates: {
    subtab: "templates",
    route: RoutesPaths.settings.projectTemplates,
    card: "project"
  },
  document_templates: {
    subtab: "templates",
    route: RoutesPaths.settings.documentTemplates,
    card: "documents"
  },
  email_templates: {
    subtab: "templates",
    route: RoutesPaths.settings.emailTemplates,
    card: "email"
  },
  drop_down_lists: {
    subtab: "templates",
    route: RoutesPaths.settings.lists,
    card: "lists"
  },
  dashboard_templates: {
    subtab: "templates",
    route: RoutesPaths.settings.dashboardTemplates,
    card: "dashboard"
  },
  lead_source_budget: {
    subtab: "templates",
    route: RoutesPaths.settings.leadSourceBudget,
    card: "leadSourceBudget",
    needsFeatureFlag: true
  }
}

export const CONFIGURATION_TABS: ConfigurationTab[] = [
  "ids",
  "projects",
  "subcontractors",
  "sms"
]

export const COMPANY_DEFAULTS_SUBTAB_ROUTES: Record<
  CompanyDefaultsSubtab,
  string
> = {
  configuration: RoutesPaths.settings.configuration,
  templates: RoutesPaths.settings.templates
}

export const COMPANY_DEFAULTS_SUBTAB_LABELS: Record<
  CompanyDefaultsSubtab,
  string
> = {
  configuration: "Configurations",
  templates: "Templates"
}

export const COMPANY_DEFAULTS_SUBTAB_ORDER: CompanyDefaultsSubtab[] = [
  "configuration",
  "templates"
]

export const blocksPlacedIn = (
  subtab: CompanyDefaultsSubtab | AutomationPlacement["subtab"]
) =>
  (
    Object.entries(COMPANY_DEFAULTS_BLOCKS) as Array<
      [CompanyDefaultsBlockKey, CompanyDefaultsPlacement]
    >
  ).filter(([, placement]) => placement.subtab === subtab)
